import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs';
import { RealtimeService } from '../services/realtime.service';
import { PushService } from '../services/push.service';
import { TimerService } from '../services/timer.service';
import { isPwaStandalone } from './msal.config';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly msal = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);
  private readonly realtime = inject(RealtimeService);
  private readonly push = inject(PushService);
  private readonly timer = inject(TimerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _account = signal<AccountInfo | null>(null);
  readonly account = this._account.asReadonly();
  // In an e2e build there is no MSAL session; the API runs the test-auth scheme,
  // so we report authenticated to let the shell render. Never true in a real build.
  readonly isAuthenticated = computed(() => environment.e2e || this._account() !== null);
  readonly displayName = computed(() => this._account()?.name ?? this._account()?.username ?? 'Guest');

  constructor() {
    // Start the SignalR hub when the user authenticates; stop it when they sign out.
    // Reactive on isAuthenticated so a redirect-flow login (which happens AFTER the
    // appInitializer ran) still triggers the hub to come up.
    effect(() => {
      if (this.isAuthenticated()) {
        this.realtime.start().catch(err => console.warn('Realtime hub start failed:', err));
        // Best-effort push subscribe. Quiet failures by design — denied/dismissed
        // never re-prompts.
        this.push.ensureSubscribed().catch(() => { /* ignore */ });
        // Phase 1 SPEC v2 §6.8 Step 3 — restore any running timer on (re)auth.
        // MUST be here, not in the cos-bar constructor: on a hard refresh the bar
        // mounts before MSAL settles its interaction state, so a constructor-time
        // GET /api/timer/current raced the token and failed silently, leaving the
        // bar un-restored (the "elapsed resets to 0" bug). This effect fires once
        // the account is confirmed, so the GET always carries a token. Error is
        // logged, never swallowed — a future failure must be visible.
        this.timer.loadCurrent().catch(err => console.warn('[timer] restore failed:', err));
      } else {
        this.realtime.stop().catch(() => { /* ignore */ });
      }
    });
  }

  initialize(): void {
    this.refreshFromCache();

    // Both subscriptions tied to DestroyRef so they unsubscribe automatically when
    // the AuthService is torn down. AuthService is providedIn: 'root', so in
    // practice it lives the lifetime of the app — but the explicit cleanup means
    // testing or future scope changes won't leak handlers.
    this.broadcast.msalSubject$
      .pipe(
        filter(e =>
          e.eventType === EventType.LOGIN_SUCCESS ||
          e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          e.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
          e.eventType === EventType.LOGOUT_SUCCESS),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshFromCache());

    this.broadcast.inProgress$
      .pipe(
        filter(s => s === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshFromCache());
  }

  login(): void {
    if (isPwaStandalone()) {
      this.msal.loginRedirect();
      return;
    }
    // Request Graph scopes alongside the API scope at sign-in so the user
    // consents to Mail.Read / User.Read once. The API's background email
    // scanner then OBO-acquires Mail.Read silently from the cached refresh
    // token — without this consent, every scanner tick fails with
    // MicrosoftIdentityWebChallengeUserException and no InboundEmail Signals
    // ever land in /triage.
    this.msal.loginPopup({
      scopes: [...environment.msal.apiScopes, ...environment.msal.graphScopes],
    }).subscribe({
      error: err => console.warn('Login popup failed:', err),
    });
  }

  logout(): void {
    if (isPwaStandalone()) {
      this.msal.logoutRedirect();
      return;
    }
    this.msal.logoutPopup().subscribe({
      error: err => console.warn('Logout popup failed:', err),
    });
  }

  private refreshFromCache(): void {
    const accounts = this.msal.instance.getAllAccounts();
    const active = this.msal.instance.getActiveAccount() ?? accounts[0] ?? null;
    if (active && !this.msal.instance.getActiveAccount()) {
      this.msal.instance.setActiveAccount(active);
    }
    this._account.set(active);
  }
}
